/**
 * ModeIndicator Component (Issue #302)
 *
 * Displays the current chat mode at the bottom of the chat panel.
 * Shows a colored chip with mode name and provides access to mode selector.
 */
import React from 'react';
import { Box, Chip, IconButton, Tooltip, alpha, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@store/index';
import {
  selectCurrentModeConfig,
  selectModeSwitching,
  setShowModeSelector,
} from '@features/chat/chatModeSlice';

// Icon mapping for modes
const MODE_ICONS: Record<string, React.ReactNode> = {
  help: <HelpOutlineIcon fontSize="small" />,
  settings: <SettingsIcon fontSize="small" />,
  ask: <QuestionAnswerIcon fontSize="small" />,
  plan: <ArchitectureIcon fontSize="small" />,
  drbfm: <AssessmentIcon fontSize="small" />,
};

interface ModeIndicatorProps {
  compact?: boolean;
}

const ModeIndicator: React.FC<ModeIndicatorProps> = ({ compact = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const currentMode = useAppSelector(selectCurrentModeConfig);
  const isSwitching = useAppSelector(selectModeSwitching);

  if (!currentMode) {
    return null;
  }

  const handleClick = () => {
    dispatch(setShowModeSelector(true));
  };

  const modeIcon = MODE_ICONS[currentMode.id] || <QuestionAnswerIcon fontSize="small" />;
  const modeName = t(`chat.modes.${currentMode.id}.name`, currentMode.name);
  const modeDescription = t(
    `chat.modes.${currentMode.id}.description`,
    currentMode.description || ''
  );

  if (compact) {
    return (
      <Tooltip title={modeDescription ? `${modeName}: ${modeDescription}` : modeName}>
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={isSwitching}
          sx={{
            color: currentMode.color,
            '&:hover': {
              backgroundColor: alpha(currentMode.color, 0.1),
            },
          }}
        >
          {modeIcon}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Tooltip title={modeDescription || ''}>
        <Chip
          icon={modeIcon as React.ReactElement}
          label={modeName}
          onClick={handleClick}
          onDelete={handleClick}
          deleteIcon={
            <ExpandMoreIcon
              sx={{
                color: `${currentMode.color} !important`,
                transform: 'rotate(0deg)',
                transition: theme.transitions.create('transform'),
              }}
            />
          }
          disabled={isSwitching}
          size="small"
          sx={{
            backgroundColor: alpha(currentMode.color, 0.1),
            borderColor: currentMode.color,
            color: currentMode.color,
            fontWeight: 500,
            '& .MuiChip-icon': {
              color: currentMode.color,
            },
            '&:hover': {
              backgroundColor: alpha(currentMode.color, 0.2),
            },
            '& .MuiChip-deleteIcon:hover': {
              color: currentMode.color,
            },
          }}
          variant="outlined"
        />
      </Tooltip>
    </Box>
  );
};

export default ModeIndicator;
